import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Slideout } from '../slideout';
import { EditableTermField } from './EditableTermField';
import SearchInput from '../search-input';
import { updateExtractedDocumentTerm, UNLINK_AMENDMENT } from '../../api/mutations';
import { getDocumentById, getDocumentsByDeal, GET_EXTRACTED_DATA_BY_TEAM } from '../../api/queries_v2';
import { useNotifications } from '../../notifications';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import styles from './ExtractedTermsSlideout.module.scss';

interface ExtractedDocumentTerm {
  id: string;
  documentType: string | null;
  classification: string;
  linkStatus?: string | null;
  anchorDocument?: {
    id: string;
    title: string;
  } | null;
  termsData: Record<string, any>;
  termsExtractedAt: string;
  userEditedFields?: string[];
  updatedAt?: string;
}

interface ExtractedTermsSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  documentName: string;
  extractedTerm: ExtractedDocumentTerm;
  canEdit: boolean;
  dealId: string;
  teamId?: string;
  documentType?: string;
  onLinkAmendment?: () => void;
}

// Helper functions - moved outside component to prevent recreation on every render
const formatFieldLabel = (fieldKey: string): string => {
  // Remove parent prefix (e.g., "financial.earnest_money" -> "earnest_money")
  const parts = fieldKey.split('.');
  const lastPart = parts[parts.length - 1];

  // Convert snake_case to Title Case
  return lastPart
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const isPrimitiveValue = (value: any): boolean => {
  return value !== null &&
         value !== undefined &&
         value !== '' &&
         value !== '—' &&
         (typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean');
};

const flattenTermsData = (data: Record<string, any>, prefix = ''): [string, any][] => {
  const result: [string, any][] = [];

  Object.entries(data || {}).forEach(([key, value]) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (isPrimitiveValue(value)) {
      result.push([fullKey, value]);
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Recursively flatten nested objects
      result.push(...flattenTermsData(value, fullKey));
    }
    // Skip arrays and other complex types
  });

  return result;
};

export const ExtractedTermsSlideout: React.FC<ExtractedTermsSlideoutProps> = ({
  isOpen,
  onClose,
  documentId,
  documentName,
  extractedTerm,
  canEdit,
  dealId,
  teamId,
  documentType,
  onLinkAmendment,
}) => {
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentlyEditingField, setCurrentlyEditingField] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showSummarySlideout, setShowSummarySlideout] = useState(false);
  // Local state to hold the current terms data with optimistic updates
  const [localTermsData, setLocalTermsData] = useState<Record<string, any>>(extractedTerm.termsData);

  const [updateTermMutation] = useMutation(updateExtractedDocumentTerm, {
    refetchQueries: [
      {
        query: getDocumentById,
        variables: { documentId },
      },
      {
        query: getDocumentsByDeal,
        variables: { dealId },
      },
    ],
  });

  const [unlinkAmendmentMutation] = useMutation(UNLINK_AMENDMENT, {
    refetchQueries: [
      {
        query: getDocumentById,
        variables: { documentId },
      },
      {
        query: getDocumentsByDeal,
        variables: { dealId },
      },
      // Refetch DataPage query if teamId and documentType are provided
      ...(teamId && documentType ? [{
        query: GET_EXTRACTED_DATA_BY_TEAM,
        variables: { teamId, documentType },
      }] : []),
    ],
  });

  // Sync local state when extractedTerm prop changes (e.g., when slideout reopened with fresh data)
  useEffect(() => {
    setLocalTermsData(extractedTerm.termsData);
  }, [extractedTerm.termsData]);

  // Click outside and scroll handlers for tooltip
  useEffect(() => {
    if (showTooltip) {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element;
        // Close if clicked outside button or tooltip
        if (!target.closest('#extracted-terms-info-btn') && !target.closest('[role="tooltip"]')) {
          setShowTooltip(false);
        }
      };

      const handleScroll = () => {
        setShowTooltip(false);
      };

      // Add listener after a brief delay to avoid immediate closure
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('scroll', handleScroll, true); // Use capture phase to catch all scrolls
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [showTooltip]);

  const handleFieldSave = useCallback(
    async (fieldKey: string, newValue: string) => {
      try {
        // Deep clone the terms data from local state
        const updatedTermsData = JSON.parse(JSON.stringify(localTermsData));

        // Handle nested keys (e.g., "financial.earnest_money")
        const keys = fieldKey.split('.');
        let current = updatedTermsData;

        // Navigate to the parent object
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }

        // Set the final value
        current[keys[keys.length - 1]] = newValue;

        // Optimistically update local state immediately
        setLocalTermsData(updatedTermsData);

        await updateTermMutation({
          variables: {
            id: extractedTerm.id,
            termsData: updatedTermsData,
          },
        });

        notifySuccess({
          message: {
            id: 'extracted-term.update.success',
            defaultMessage: 'Term updated successfully',
          },
        });
      } catch (error) {
        console.error('Failed to update term:', error);
        // Revert optimistic update on error
        setLocalTermsData(extractedTerm.termsData);
        notifyError({
          message: {
            id: 'extracted-term.update.error',
            defaultMessage: 'Failed to update term. Please try again.',
          },
        });
        throw error;
      }
    },
    [localTermsData, extractedTerm.id, extractedTerm.termsData, updateTermMutation, notifySuccess, notifyError]
  );

  const handleUnlinkAmendment = useCallback(
    async () => {
      try {
        await unlinkAmendmentMutation({
          variables: {
            amendmentId: extractedTerm.id,
          },
        });

        notifySuccess({
          message: 'Amendment unlinked successfully',
        });
      } catch (error) {
        console.error('Failed to unlink amendment:', error);
        notifyError({
          message: 'Failed to unlink amendment. Please try again.',
        });
      }
    },
    [extractedTerm.id, unlinkAmendmentMutation, notifySuccess, notifyError]
  );

  const formatClassification = (classification: string): string => {
    if (classification === 'base') return 'Base Document';
    if (classification === 'amendment') return 'Amendment';
    return classification.charAt(0).toUpperCase() + classification.slice(1);
  };

  const formatExtractedDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const getFieldType = (fieldKey: string, value: any): 'text' | 'date' | 'number' | 'textarea' | 'boolean' => {
    // Boolean fields: detect boolean type values
    if (typeof value === 'boolean') {
      return 'boolean';
    }

    // Date fields: check field name patterns
    if (fieldKey.includes('date') || fieldKey.includes('_on') || fieldKey.includes('_at')) {
      return 'date';
    }

    // Number fields: ONLY use number input if value is actually a number type
    // This allows formatted strings like "$5,250,000" to remain as text
    if (typeof value === 'number') {
      return 'number';
    }

    // Long strings get textarea for better editing experience
    if (typeof value === 'string' && value.length > 100) {
      return 'textarea';
    }

    // Everything else is text (including formatted currency, descriptions, etc)
    return 'text';
  };

  // Sort fields alphabetically by label and filter by search
  const sortedAndFilteredFields = useMemo(() => {
    const flattened = flattenTermsData(localTermsData || {})
      .filter(([fieldKey]) => fieldKey !== 'document_abstract'); // Exclude document_abstract - it's only for View Summary
    const sorted = flattened.sort(([keyA], [keyB]) => formatFieldLabel(keyA).localeCompare(formatFieldLabel(keyB)));

    if (!searchQuery.trim()) return sorted;

    const query = searchQuery.toLowerCase();
    return sorted.filter(([fieldKey, fieldValue]) => {
      const label = formatFieldLabel(fieldKey).toLowerCase();
      const value = String(fieldValue).toLowerCase();
      return label.includes(query) || value.includes(query);
    });
  }, [localTermsData, searchQuery]);

  const formatDocumentType = (type: string | null): string => {
    if (!type) return '';
    return type.toUpperCase();
  };

  return (
    <Slideout isOpen={isOpen} onClose={onClose} title={documentName} width="medium">
      {/* Sticky Header Content */}
      <div className={styles.stickyHeader}>
        {/* Metadata */}
        <div className={styles.metadata}>
          <div className={styles.metadataRow}>
            <span className={styles.metadataLabel}>Classification:</span>
            <span className={styles.metadataValue}>{formatClassification(extractedTerm.classification)}</span>
            {extractedTerm.classification === 'amendment' && extractedTerm.linkStatus === 'pending_link' && onLinkAmendment && (
              <button
                className={styles.linkAmendmentButton}
                onClick={onLinkAmendment}
                type="button"
              >
                ⚠️ Link to Base
              </button>
            )}
          </div>
          {extractedTerm.classification === 'amendment' && extractedTerm.anchorDocument && (
            <div className={styles.metadataRow}>
              <span className={styles.metadataLabel}>Linked to:</span>
              <span className={styles.metadataValue} title={extractedTerm.anchorDocument.title}>
                {extractedTerm.anchorDocument.title}
              </span>
              <button
                className={styles.unlinkAmendmentButton}
                onClick={handleUnlinkAmendment}
                type="button"
              >
                (remove link)
              </button>
            </div>
          )}
          {extractedTerm.documentType && (
            <div className={styles.metadataRow}>
              <span className={styles.metadataLabel}>Document Type:</span>
              <span className={styles.metadataValue}>{formatDocumentType(extractedTerm.documentType)}</span>
            </div>
          )}
          <div className={styles.metadataRow}>
            <span className={styles.metadataLabel}>Extracted:</span>
            <span className={styles.metadataValue}>
              {formatExtractedDate(extractedTerm.termsExtractedAt)}
            </span>
            {localTermsData?.document_abstract && (
              <button
                className={styles.viewSummaryButton}
                onClick={() => setShowSummarySlideout(true)}
                type="button"
              >
                View Summary
              </button>
            )}
          </div>
        </div>

        {/* Full-width divider above Extracted Terms */}
        <div className={styles.fullWidthDivider} />

        {/* Section Title with Search Field inline */}
        <div className={styles.titleRow}>
          <div className={styles.titleWithTooltip}>
            <h3 className={styles.sectionTitle}>Extracted Terms</h3>
            <button
              id="extracted-terms-info-btn"
              className={styles.infoButton}
              onClick={() => setShowTooltip(!showTooltip)}
              aria-label="Extracted data disclaimer"
            >
              !
            </button>
            <ReactTooltip
              anchorSelect="#extracted-terms-info-btn"
              place="bottom"
              isOpen={showTooltip}
              clickable
              border="1px solid #e0e0e0"
              style={{
                backgroundColor: 'rgba(221, 235, 239, 1)',
                color: '#18191b',
                maxWidth: '300px',
                fontSize: '14px',
                padding: '12px 14px',
                borderRadius: '8px',
                zIndex: 10000,
              }}
            >
              Extracted data is AI-generated and may contain inaccuracies. Please verify all important information before use.
            </ReactTooltip>
          </div>
          <div className={styles.searchSection}>
            <SearchInput
              className=""
              placeholder="Search terms..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Content-width divider below Extracted Terms */}
        <div className={styles.contentDivider} />
      </div>

      {/* Scrollable Terms List */}
      <div className={styles.termsSection}>
        {sortedAndFilteredFields.length === 0 ? (
          <div className={styles.emptyState}>
            {searchQuery ? 'No terms match your search.' : 'No terms were extracted from this document.'}
          </div>
        ) : (
          <div className={styles.termsList}>
            {sortedAndFilteredFields.map(([fieldKey, fieldValue]) => (
              <EditableTermField
                key={fieldKey}
                fieldKey={fieldKey}
                label={formatFieldLabel(fieldKey)}
                value={fieldValue}
                onSave={(newValue) => handleFieldSave(fieldKey, newValue)}
                disabled={!canEdit}
                type={getFieldType(fieldKey, fieldValue)}
                currentlyEditingField={currentlyEditingField}
                onStartEdit={setCurrentlyEditingField}
              />
            ))}
          </div>
        )}
      </div>

      {/* Document Summary Slideout - rendered on top when visible */}
      {showSummarySlideout && localTermsData?.document_abstract && (
        <Slideout
          isOpen={showSummarySlideout}
          onClose={() => setShowSummarySlideout(false)}
          title="Document Summary"
          width="medium"
        >
          <div className={styles.summaryContent}>
            <div className={styles.summaryDisclaimer}>
              <span className={styles.disclaimerIcon}>!</span>
              <span>This summary is AI-generated and may contain inaccuracies. Please verify important information.</span>
            </div>
            <div className={styles.summaryText}>
              {localTermsData.document_abstract.split('\n').map((paragraph: string, index: number) => (
                paragraph.trim() ? <p key={index}>{paragraph}</p> : <br key={index} />
              ))}
            </div>
          </div>
        </Slideout>
      )}
    </Slideout>
  );
};
